// src/pages/communication/schedule/ScheduleNew.jsx
import React, { useState, useEffect } from "react";
import api from "../../../api/axios";
import { scheduleApi } from "../../../api/schedules";
import ReferenceModal from "../../../components/approval/ReferenceModal";

export default function ScheduleNew({
  projectId,
  companyId,
  initial,
  defaultScope = "personal",
  mode = "create",
  onSaved,
  onClose,
}) {
  const [scope, setScope] = useState(initial?.scope ?? defaultScope);

  const [date, setDate] = useState(initial?.start?.slice(0, 10) || "");
  const [time, setTime] = useState(initial?.start?.slice(11, 16) || "");
  const [form, setForm] = useState({
    title: initial?.title || "",
    memo: initial?.memo || "",
  });

  const [users, setUsers] = useState([]);
  const [participantIds, setParticipantIds] = useState(
    initial?.participants?.map((p) => Number(p.id)) || []
  );
  const [modalOpen, setModalOpen] = useState(false);

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  useEffect(() => {
    (async () => {
      try {
        let res;
        if (scope === "project" || scope === "company") {
          res = await api.get(`/project/${projectId}/users-without-me/`);
          const members = res.data?.results ?? res.data ?? [];
          setUsers(members);
        } else {
          setUsers([]);
        }
      } catch (err) {
        console.error("사용자 목록 조회 실패", err);
      }
    })();
  }, [scope, projectId, companyId]);

  const submit = async () => {
    if (!form.title.trim()) return alert("제목을 입력해주세요.");
    if (!date) return alert("날짜를 선택해주세요.");
    if (!time) return alert("시간을 선택해주세요.");

    const isoStart = new Date(`${date}T${time}:00`).toISOString();

    const basePayload = {
      title: form.title,
      start: isoStart,
      memo: form.memo,
      scope,
      participant_ids: scope === "personal" ? [] : participantIds,
      project: projectId,
    };

    const payload =
      scope === "company" && companyId
        ? { ...basePayload, company: companyId }
        : basePayload;

    try {
      const { data } =
        mode === "create"
          ? await scheduleApi.create(payload)
          : await scheduleApi.update(initial.id, payload);

      onSaved?.(data);
    } catch (err) {
      console.error("저장 실패", err);
      alert("저장 실패");
    }
  };

  const userById = new Map(users.map((u) => [Number(u.id), u]));

  return (
    <div className="space-y-7">
      <div>
        <h3 className="text-xl font-semibold text-slate-900">
          {mode === "create" ? "일정 등록" : "일정 수정"}
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          일정 정보를 입력해주세요.
        </p>
      </div>

      {/* SCOPE SELECTOR */}
      <div className="flex gap-1.5 bg-slate-100 rounded-lg p-1 w-fit">
        {(() => {
          const companyLabel = companyId ? `회사(${companyId})` : "참여사";
          const options = [
            { key: "project", label: "현장" },
            { key: "company", label: companyLabel },
            { key: "personal", label: "개인" },
          ];

          return options.map((opt) => {
            const active = scope === opt.key;
            const locked = mode === "edit";

            return (
              <button
                key={opt.key}
                disabled={locked}
                onClick={() => {
                  if (locked) return;
                  setScope(opt.key);
                  setParticipantIds([]);
                }}
                className={`
                  px-4 py-1.5 rounded-md text-sm transition
                  ${
                    active
                      ? "bg-gray-600 text-white shadow-sm"
                      : "text-slate-600 hover:bg-white"
                  }
                  ${locked ? "opacity-50 cursor-not-allowed" : ""}
                `}
              >
                {opt.label}
              </button>
            );
          });
        })()}
      </div>

      <div className="border rounded-lg p-4">
        <div className="space-y-1.5 mb-4">
          <label className="text-body font-medium">제목</label>
          <input
            name="title"
            className="input-base"
            value={form.title}
            onChange={onChange}
          />
        </div>

        <div className="space-y-1.5 mb-4">
          <label className="text-body font-medium">일시</label>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="date"
              className="input-base"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            <input
              type="time"
              className="input-base"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>
        </div>

        {scope !== "personal" && (
          <div className="space-y-2 mb-4">
            <label className="text-body font-medium">참여자</label>

            <button
              type="button"
              className="ml-2 btn-basic"
              onClick={() => setModalOpen(true)}
            >
              참여자 선택
            </button>

            <div className="flex flex-wrap gap-2">
              {participantIds.length === 0 ? (
                <span className="text-sm text-slate-400">
                  선택된 참여자가 없습니다.
                </span>
              ) : (
                participantIds.map((id) => {
                  const u = userById.get(id);
                  const name = u
                    ? `${u.last_name}${u.first_name} ${u.position ?? ""}`
                    : id;

                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-xs rounded-full"
                    >
                      {name}
                      <button
                        onClick={() =>
                          setParticipantIds((prev) =>
                            prev.filter((x) => x !== id)
                          )
                        }
                      >
                        ✕
                      </button>
                    </span>
                  );
                })
              )}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-body font-medium">메모</label>
          <textarea
            name="memo"
            className="input-base"
            rows={4}
            value={form.memo}
            onChange={onChange}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button className="btn-create" onClick={submit}>
          {mode === "create" ? "등록" : "저장"}
        </button>
        <button className="btn-basic" onClick={onClose}>
          닫기
        </button>
      </div>

      <ReferenceModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        users={users}
        selectedIds={participantIds}
        scheduleType={scope}
        companyId={companyId}
        onChange={setParticipantIds}
        onSave={(ids, detailUsers) => {
          setParticipantIds(ids);
          const map = new Map(users.map((u) => [u.id, u]));
          detailUsers.forEach((u) => map.set(u.id, u));
          setUsers([...map.values()]);
          setModalOpen(false);
          return true;
        }}
        name="참여자"
      />
    </div>
  );
}
